import Delete from "@mui/icons-material/Delete";
import { type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { CircularProgress, Container, Stack, Switch, Typography } from "@mui/material";
import axios, { type AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectOrganizationId } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import useCoAppDataGridFilters from "../../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../../SettingsDataGridSelectionRow";
import { getPluginGroupsColumns, type PluginGroupRow } from "./columns/plugin-groups-columns";

/** Passed from `PluginGroupsWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface PluginGroupsNavState {
	name: string;
	isActive?: boolean;
	isCustomerManaged?: boolean;
}

interface PluginGroupsProps {
	state?: PluginGroupsNavState | null;
}

export default function PluginGroups({ state: stateFromBoundary }: PluginGroupsProps = {}) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("pluginGroups");
	const { updateCoAppNavigation } = useCoAppNavigation();
	const { pluginId } = useParams();
	const { state: stateFromLocation } = useLocation() as { state: PluginGroupsNavState | undefined };
	const state = (stateFromBoundary ?? stateFromLocation) as PluginGroupsNavState | undefined;
	const organizationId = useSelector(selectOrganizationId);

	const pluginName = state?.name ?? (pluginId ? `Plugin ${pluginId}` : "Plugin");

	const [pluginActive, setPluginActive] = useState(state?.isActive ?? false);
	const [pluginGroups, setPluginGroups] = useState<PluginGroupRow[]>([]);
	const [selectedGroups, setSelectedGroups] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [bulkRemoveGroupsDialogOpen, setBulkRemoveGroupsDialogOpen] = useState(false);
	const [removeGroupDialogOpen, setRemoveGroupDialogOpen] = useState(false);
	const [groupBeingRemoved, setGroupBeingRemoved] = useState<PluginGroupRow | null>(null);
	const [addGroupsOpen, setAddGroupsOpen] = useState(false);
	const [groupsToAdd, setGroupsToAdd] = useState([]);
	const [availableGroups, setAvailableGroups] = useState([]);
	const [loading, setLoading] = useState(true);
	const [optionsLoading, setOptionsLoading] = useState(true);
	
	const handleOpenRemoveGroupConfirmDialog = (group: PluginGroupRow) => {
		setRemoveGroupDialogOpen(true);
		setGroupBeingRemoved(group);
	};

	const columns = useMemo(() => (
		getPluginGroupsColumns(
			handleOpenRemoveGroupConfirmDialog
		)
	), []);

	const initPluginGroups = () => {
		axios.get(apiRoutes.getGroupsForPlugin(pluginId as unknown as string), { headers: authHeader })
			.then((response) => {
				setPluginGroups(response.data);
				setLoading(false);
			})
			.catch((error) => {
				console.error("Error fetching plugin groups: ", error);
			});
	};

	const initPluginStatus = () => {
		setPluginActive(Boolean(state?.isActive));
	};

	const handlePluginStatusToggleChange = () => {
		if (!pluginId || organizationId == null) {
			return;
		}
		const data = {
			patchData: { active: !pluginActive },
			organizationId
		};
		axios.patch(apiRoutes.patchPlugin(pluginId), data, { headers: authHeader })
			.then(() => {
				setPluginActive(!pluginActive);
			})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_PLUGIN_TOGGLE_ERROR_MSG);
			});
	};

	const toggleAddGroupsDialog = () => {
		if (!addGroupsOpen) {
			setAddGroupsOpen(true);
			initAvailableGroups();
		} else {
			setAddGroupsOpen(false);
			setGroupsToAdd([]);
		}
	};

	const initAvailableGroups = () => {
		setOptionsLoading(true);
		axios.get(apiRoutes.getGroups, { headers: authHeader })
			.then((response) => {
				const availableGroups = response.data.filter((group: any) => {
					return !pluginGroups.some(pluginGroup => pluginGroup.id === group.id);
				});
				setAvailableGroups(availableGroups);
				setOptionsLoading(false);
			})
			.catch((error: AxiosError) => {
				console.error("Error fetching available groups: ", error);
			});
	};

	const addGroupsToPlugin = () => {
		if (groupsToAdd.length === 0) return;
		const groupIds = groupsToAdd.map((group: any) => group.id);
		const payload = {
			groups: groupIds
		};
		axios.post(apiRoutes.addGroupsToPlugin(pluginId as unknown as string), payload, { headers: authHeader })
			.then(() => {
				toggleAddGroupsDialog();
				initPluginGroups();
			})
			.catch((error) => {
				console.error("Error adding groups to plugin: ", error);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_ADDED_ERROR_MSG);
			});
	};

	const bulkRemoveGroups = () => {
		const groupIds = gridRowSelectionIdsArray(selectedGroups, pluginGroups);
		if (groupIds.length === 0) return;
		const deleteData = { groupIds };
		axios.delete(apiRoutes.bulkRemoveGroupsFromPlugin(pluginId as unknown as string), { headers: authHeader, data: deleteData })
			.then(() => {
				setBulkRemoveGroupsDialogOpen(false);
				initPluginGroups();
				setSelectedGroups(emptyGridRowSelectionModel());
			})
			.catch((err) => {
				console.error(err);
				setBulkRemoveGroupsDialogOpen(false);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_REMOVAL_ERROR_MSG);
			});
	};

	const removePluginGroup = () => {
		if (!pluginId || !groupBeingRemoved) return;
		axios.delete(apiRoutes.removeGroupFromPlugin(pluginId, groupBeingRemoved.id), { headers: authHeader })
			.then(() => {
				initPluginGroups();
				setRemoveGroupDialogOpen(false);
				setGroupBeingRemoved(null);
			})
			.catch((err: AxiosError) => {
				console.error(err);
				setRemoveGroupDialogOpen(false);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_REMOVAL_ERROR_MSG);
			});
	};

	const addGroupsToGroupsToAdd = (value: any) => {
		value = value.map((groupName: any) => {
			const foundGroup = availableGroups.find((group: any) => group.name === groupName);
			return foundGroup;
		});
		setGroupsToAdd(value);
	};

	useEffect(() => {
		initPluginStatus();
		initPluginGroups();
		handleInitializeSortModel();
		updateCoAppNavigation(false, navigationItemReferences.pluginNavItemIndex, pluginName);
	}, [pluginName]);

	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(pluginGroups, loading);
	const selectedGroupCount = resolveGridSelectedRowIds(selectedGroups, pluginGroups).length;

	if (pluginId) {
		return (
			<>
				<CoAppDatagridHeader
					title={`${pluginName} Group Management`}
					subResourceType="Group"
					resourceType="Plugin"
					searchIsPresent={true}
					{...quickFilterSearchProps}
					searchPlaceholder="Search all columns…"
					filterControl={filterControl}
					headerSecondaryComponent={
						<Stack direction="row" sx={{ alignItems:"center" }} spacing={0.5}>
							<Typography sx={{ fontSize: 13, fontWeight: 500, color: "rgba(13, 31, 48, 0.65)" }}>
								Active
							</Typography>
							<Switch
								checked={pluginActive}
								onChange={handlePluginStatusToggleChange}
							/>
						</Stack>
					}
					addIsPresent={true}
					addOnClickHandler={toggleAddGroupsDialog}
					editIsPresent={false}
					deleteIsPresent={false}
				/>
				<CoAppStandardDataGrid
					apiRef={apiRef}
					filterButtonEl={filterButtonEl}
					filterModel={gridFilterModel}
					onFilterModelChange={onFilterModelChange}
					visibleRowCount={visibleRowCount}
					columns={columns}
					rows={pluginGroups}
					pinnedColumns={["actions"]}
					allowSelection={true}
					targetResource="groups"
					parentResource="plugin"
					parentResourceName={pluginName}
					selectionModel={selectedGroups}
					setSelectionModel={setSelectedGroups}
					loading={loading}
					sortModel={sortModel}
					handleSortModelChange={handleSortModelChange}
					selectionFooter={
						<SettingsDataGridSelectionRow
							resourceNoun="group"
							visibleCount={visibleRowCount}
							selectedCount={selectedGroupCount}
							loading={loading}
							onClearSelection={() => setSelectedGroups(emptyGridRowSelectionModel())}
							actions={[{
								label: "Remove",
								icon: <Delete sx={destructiveIconSx} />,
								onClick: () => setBulkRemoveGroupsDialogOpen(true),
								variant: "destructive",
							}]}
						/>
					}
				/>
				<CoAppAddItemsToListModal
					addButtonTitle="Save"
					addItemsIsToggled={addGroupsOpen}
					addItemsToCollection={addGroupsToPlugin}
					addItemsToNewArray={addGroupsToGroupsToAdd}
					dialogTitle={`Add group(s) to ${pluginName} plugin`}
					inputLabel="Group(s)"
					items={availableGroups}
					itemsToAdd={groupsToAdd}
					toggleDialog={toggleAddGroupsDialog}
					loading={optionsLoading}
				/>
				<CoAppDestructiveConfirmationModal
					dialogOpen={removeGroupDialogOpen}
					dialogTitle={`Remove ${groupBeingRemoved?.name ?? ""} group from ${pluginName} plugin?`}
					dialogMessage={`Are you sure that you want to remove group ${groupBeingRemoved?.name ?? ""} from the ${pluginName} plugin?`}
					confirmClickHandler={removePluginGroup}
					cancelClickHandler={() => {
						setRemoveGroupDialogOpen(false);
						setGroupBeingRemoved(null);
					}}
					actionText="Remove"
				/>
				<CoAppDestructiveConfirmationModal
					dialogOpen={bulkRemoveGroupsDialogOpen}
					dialogTitle={`Remove selected ${gridRowSelectionSize(selectedGroups, pluginGroups) > 1 ? "groups" : "group"} from ${pluginName} plugin?`}
					dialogMessage={`Are you sure that you want to remove the selected ${gridRowSelectionSize(selectedGroups, pluginGroups) > 1 ? "groups" : "group"} from the ${pluginName} plugin?`}
					confirmClickHandler={bulkRemoveGroups}
					cancelClickHandler={() => setBulkRemoveGroupsDialogOpen(false)}
					actionText="Remove"
				/>
			</>
		);
	} else {
		return (
			<Container sx={{ maxWidth: "150px!important", marginTop: "50px" }}>
				<Stack spacing={2}>
					<CircularProgress sx={{ color: "#2FBD70" }} />
				</Stack>
			</Container>
		);
	}
}